import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { getUserColor } from '@/core/lib/get-user-color';
import { plannedToAnchorDate } from '@/shared/lib/datetime/planned-date';
import { TimelineTaskBar } from './TimelineTaskBar';
import { TimelineMilestoneMarker } from './TimelineMilestoneMarker';
import { TimelineAbsenceBar } from './TimelineAbsenceBar';
import {
  computeBarPosition,
  computeMarkerOffset,
  TIMELINE_PERSON_COLUMN_WIDTH_PX,
  TIMELINE_ROW_HEIGHT_PX,
} from '../../utils/timeline-geometry';
import type { TimelinePersonRowModel } from '../../types';

interface TimelinePersonRowProps {
  row: TimelinePersonRowModel;
  columnCount: number;
  columnWidthPx: number;
  windowStart: Date;
  windowEnd: Date;
  onTaskClick: (taskId: string) => void;
}

/** One person's swimlane: avatar + name, then their task bars, milestones and absence blocks. */
export function TimelinePersonRow({
  row,
  columnCount,
  columnWidthPx,
  windowStart,
  windowEnd,
  onTaskClick,
}: TimelinePersonRowProps) {
  const totalWidthPx = columnCount * columnWidthPx;

  return (
    <div
      data-testid="tasks-timeline-person-row"
      className="flex items-stretch border-b border-border"
      style={{ height: TIMELINE_ROW_HEIGHT_PX }}
    >
      <div
        className="sticky left-0 z-10 flex shrink-0 items-center gap-2 border-r border-border bg-background pl-3.5"
        style={{ width: TIMELINE_PERSON_COLUMN_WIDTH_PX }}
      >
        <UserAvatar
          name={row.person.name}
          color={getUserColor(row.person.id)}
          src={row.person.avatarUrl}
          size="sm"
        />
        <p className="truncate text-[13px] font-medium text-foreground">{row.person.name}</p>
      </div>

      <div className="relative shrink-0" style={{ width: totalWidthPx }}>
        {row.tasks.map((task) => {
          const position = computeBarPosition(
            task.startDate,
            task.endDate,
            windowStart,
            windowEnd,
            totalWidthPx,
          );
          if (!position) return null;
          return (
            <TimelineTaskBar
              key={task.id}
              task={task}
              leftPx={position.leftPx}
              widthPx={position.widthPx}
              onClick={onTaskClick}
            />
          );
        })}

        {row.absenceBlocks.map((block) => {
          const position = computeBarPosition(
            block.startDate,
            block.endDate,
            windowStart,
            windowEnd,
            totalWidthPx,
          );
          if (!position) return null;
          return (
            <TimelineAbsenceBar
              key={`${block.userId}-${block.startDate.toISOString()}`}
              block={block}
              leftPx={position.leftPx}
              widthPx={position.widthPx}
            />
          );
        })}

        {row.milestones.map((milestone) => {
          const date = plannedToAnchorDate(milestone.date);
          const offset = computeMarkerOffset(date, windowStart, windowEnd, totalWidthPx);
          if (offset == null) return null;
          return (
            <TimelineMilestoneMarker
              key={`${milestone.id}-${milestone.personUserId}`}
              milestone={milestone}
              leftPx={offset}
            />
          );
        })}
      </div>
    </div>
  );
}
